// Packages
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from "react-toastify"

// Models
import useModels from '../../../models';

// Helpers
import useHelpers from '../../../helpers';

// Actions
import useActions from '../../../api/actions';

// Components
import useComponents from '../../../views/components';

// Config
import useConfig from '../../../config';

// ============================================
// TOGGLE: Auto-redirect to Keycloak login
// Set to true to automatically redirect to Keycloak
// Set to false to show manual login button
// ============================================
const AUTO_REDIRECT_TO_KEYCLOAK = false;

const useLogin = () => {
  const [isCheckingKeycloak, setIsCheckingKeycloak] = useState(true);
  const [isProcessingKeycloakCallback, setIsProcessingKeycloakCallback] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Helpers
  const { useQuickFunctions } = useHelpers();
  const { useValidators, useNavigation, useToast } = useQuickFunctions();
  const { loginValidatorSchema } = useValidators();
  const { replaceAndNavigateTo, navigateTo } = useNavigation();
  const { success, error: toastError } = useToast();

  // Components
  const { Toast } = useComponents();

  // Config
  const { useKeycloak } = useConfig();
  const { initKeycloakOnLoad, getKeycloakUserInfo, getKeycloakToken, resetKeycloakInstance } = useKeycloak();

  // Actions
  const { dispatch, useAuthActions } = useActions();
  const { actLogin, actValidLoginPassword, actKeycloakLogin, actSetMfaPending, actVerifyCredentials } = useAuthActions();

  // Models
  const { useSelectors } = useModels();
  const { useSelector, useAuthSelectors } = useSelectors();
  const { session } = useAuthSelectors();

  const { authToken, isKeycloakAuth } = useSelector(session);

  const {
    control,
    handleSubmit,
    watch
  } = useForm({
    mode: 'all',
    resolver: yupResolver(loginValidatorSchema),
  });

  const watchFieldsEmail = watch(["email"]);

  const handleForgot = () => {
    replaceAndNavigateTo("/contrasena/olvidar", {email: watchFieldsEmail})
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  }

  const onSubmit = useCallback(
    ({email, password}) => {
      // First verify credentials before redirecting to MFA
      const onSuccessVerify = () => {
        // Credentials are valid, store them and redirect to MFA
        dispatch(actSetMfaPending({ email, password }));
        navigateTo("/mfa");
      };

      const onErrorVerify = (error) => {
        // Credentials are invalid
        const errorMessage = error?.response?.data?.message ||
                            error?.message ||
                            "Las credenciales son incorrectas.";
        toastError(<Toast text={errorMessage} />, {
          toastId: 0,
        });
      };

      actVerifyCredentials({ email, password }, onSuccessVerify, onErrorVerify);
    }, [dispatch, actSetMfaPending, actVerifyCredentials, navigateTo, toastError]
  )

  const handleKeycloakLogin = useCallback(() => {
    // If already logged in with Keycloak, just navigate
    if (authToken && isKeycloakAuth) {
      navigateTo("/dashboard");
      return;
    }

    const onSuccessKeycloakLogin = (data) => {
      success(<Toast text="Inicio de sesión exitoso con Keycloak" />, {
        toastId: 0,
      });
      // Use setTimeout to ensure Redux state is fully updated before navigation
      setTimeout(() => {
        navigateTo("/dashboard");
      }, 200);
    };

    const onErrorKeycloakLogin = (error) => {
      toastError(<Toast text="Error al iniciar sesión con Keycloak" />, {
        toastId: 0,
      });
    };

    dispatch(actKeycloakLogin(onSuccessKeycloakLogin, onErrorKeycloakLogin));
  }, [dispatch, actKeycloakLogin, navigateTo, authToken, isKeycloakAuth, success, toastError]);

  // Check for existing Keycloak session on mount
  useEffect(() => {
    // Check if there's a Keycloak callback in the URL (query string or hash fragment)
    const hasKeycloakParams = window.location.search.includes('state') ||
                              window.location.search.includes('session_state') ||
                              window.location.search.includes('code') ||
                              window.location.hash.includes('state') ||
                              window.location.hash.includes('session_state') ||
                              window.location.hash.includes('code');

    if (hasKeycloakParams) {
      setIsProcessingKeycloakCallback(true);
      // We're returning from Keycloak, initialize and handle the callback
      initKeycloakOnLoad(
        async (kc) => {
          if (kc && kc.authenticated) {
            const token = getKeycloakToken();

            try {
              // Fetch user profile from /user endpoint via action
              const onSuccessProfile = () => {
                success(<Toast text="Inicio de sesión exitoso con Keycloak" />, {
                  toastId: 0,
                });

                // Use setTimeout to ensure Redux state is fully updated before navigation
                setTimeout(() => {
                  navigateTo("/dashboard");
                }, 200);
              };

              const onErrorProfile = (error) => {
                toastError(<Toast text="Error al obtener perfil de usuario" />, {
                  toastId: 0,
                });
              };

              // Use the same flow as clicking the Keycloak login button
              // But we need to manually trigger the profile fetch since we're already authenticated
              dispatch(actKeycloakLogin(onSuccessProfile, onErrorProfile));
            } catch (error) {
              // Error handled by action
            }
          }
          setIsCheckingKeycloak(false);
          setIsProcessingKeycloakCallback(false);
        },
        (error) => {
          // Silently handle error (expected when processing stale params after logout)
          // Reset Keycloak instance to allow fresh login attempt
          resetKeycloakInstance();

          // Clean stale Keycloak params from URL after error
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);

          setIsCheckingKeycloak(false);
          setIsProcessingKeycloakCallback(false);
        }
      );
    } else {
      // No Keycloak params, just set checking to false
      setIsCheckingKeycloak(false);
    }
  }, []);

  // Separate effect to handle automatic navigation for already logged-in users
  useEffect(() => {
    // Wait for the checking to complete
    if (!isCheckingKeycloak && authToken) {
      // Check if it's a Keycloak auth or traditional auth
      if (isKeycloakAuth) {
        // For Keycloak users, navigate to dashboard
        navigateTo("/dashboard");
      } else {
        // For traditional users, validate first
        dispatch(actValidLoginPassword()).then((isValid) => {
          if (isValid) {
            navigateTo("/dashboard");
          }
        });
      }
    }
  }, [isCheckingKeycloak, authToken, isKeycloakAuth]);

  // Auto-redirect to Keycloak login if enabled
  useEffect(() => {
    // Don't auto-redirect if we're already processing a Keycloak callback
    if (AUTO_REDIRECT_TO_KEYCLOAK && !isCheckingKeycloak && !authToken && !isProcessingKeycloakCallback) {
      // Small delay to ensure component is mounted
      const timer = setTimeout(() => {
        handleKeycloakLogin();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCheckingKeycloak, authToken, isProcessingKeycloakCallback]);

  return {
    control,
    handleSubmit,
    onSubmit,
    handleForgot,
    handleKeycloakLogin,
    isCheckingKeycloak,
    autoRedirectEnabled: AUTO_REDIRECT_TO_KEYCLOAK,
    showPassword,
    handleTogglePasswordVisibility,
  }
}

export default useLogin;